import styled from '@emotion/styled';
import Downshift from 'downshift';
import React, {
  type HTMLAttributes,
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useRef,
  useCallback,
} from 'react';

import { useLayer } from 'react-laag';
import ResizeObserver from 'resize-observer-polyfill';

import type { LensDivProps } from '../../types';

import { getDownshiftEnvironment } from '../../utilities/downshift';
import { getColorValue, getSize, u } from '../../utilities';
import { SvgChevronDown } from '../icon/available-icons/chevron-down';
import Icon from '../icon/icon';

import Menu, { MenuItem, menuPositions } from '../menu/menu';
import Align from '../align/align';
import Container from '../container/container';
import Loader from '../loader/loader';
import Spacer from '../spacer/spacer';
import Text from '../text/text';

const TypeaheadWrapper = styled.div`
  position: relative;
`;

const Group = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ErrorMessage = styled.span`
  color: var(--lns-color-red);
  margin-top: var(--lns-space-xsmall);
  display: block;
  width: 100%;
  grid-column-start: 1;
  grid-column-end: 3;
`;

const isGrouped = (
  options: OptionsObject[] | GroupedOptionsObject[]
): options is GroupedOptionsObject[] =>
  Array.isArray(options) && options.length > 0 && 'group' in options[0];

const getSelectedOptionObject = ({ options, selectedOptionValue }) => {
  if (!options || !selectedOptionValue) {
    return { icon: null, title: null };
  }

  if (isGrouped(options)) {
    for (const option of options) {
      const foundItem = option.items.find(
        item => item.value === selectedOptionValue
      );
      if (foundItem) {
        return foundItem;
      }
    }
  } else {
    const foundItem = options.find(item => item.value === selectedOptionValue);
    return foundItem || { icon: null, title: null };
  }

  return { icon: null, title: null };
};

const getIcon = ({ options, selectedItem, selectedOptionValue }) => {
  if (selectedItem) {
    return selectedItem.icon;
  }

  if (selectedOptionValue) {
    return getSelectedOptionObject({ options, selectedOptionValue }).icon;
  }
};

const getTitle = ({
  options,
  selectedItem,
  selectedOptionValue,
  placeholder,
}) => {
  if (selectedItem) {
    return selectedItem.title;
  }

  if (selectedOptionValue) {
    return getSelectedOptionObject({ options, selectedOptionValue }).title;
  }

  return placeholder;
};

const getHeaderAccessibilityProps = ({
  selectedItem,
  getInputProps,
  ariaMenuName,
}) => {
  const valueLabel = selectedItem
    ? `selected value is ${selectedItem.title}`
    : 'no value selected';

  return {
    // Ensures all options within the list of selections can be narrated by a screen reader
    'aria-activedescendant': getInputProps()['aria-activedescendant'],
    // The existing aria-label includes whether or not the menu is open or closed, and we add the valueLabel to make a screen reader narrate what is selected
    'aria-label': [ariaMenuName, valueLabel].filter(Boolean).join(', '),
  };
};

const TypeaheadHeaderWrapper = styled.button`
  position: relative;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const TypeaheadInputField = styled.input<{
  hasIcon?: boolean;
  hasAddOn?: boolean;
  hasError?: boolean;
  isDisabled?: boolean;
}>`
  -webkit-appearance: none;
  font-family: inherit;
  width: 100%;
  height: var(--lns-formFieldHeight);
  border: none;
  color: inherit;
  background-color: ${getColorValue('formFieldBackground')};
  transition: 0.3s box-shadow;
  padding-top: 0;
  padding-bottom: 0;
  id: ${props => props.id};
  padding-left: ${props => (props.hasIcon ? u(5.5) : 'var(--ds-space-150)')};
  padding-right: ${props => (props.hasAddOn ? u(5.5) : 'var(--ds-space-150)')};
  border-radius: var(--ds-radius-large, var(--lns-formFieldRadius));
  box-shadow: inset 0 0 0
    ${props =>
      props.hasError
        ? 'var(--lns-formFieldBorderWidthFocus) var(--lns-color-danger)'
        : 'var(--lns-formFieldBorderWidth) var(--lns-color-formFieldBorder)'};

  ${getSize('font-size', 'medium')};

  &:hover:not(:disabled):not(:focus) {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidthFocus)
      ${props =>
        props.hasError
          ? 'var(--lns-color-danger)'
          : 'var(--lns-color-blurple)'};
  }

  &:focus {
    outline: 1px solid transparent;
    box-shadow: var(--lns-formFieldBorderShadowFocus);
  }

  &:disabled {
    color: ${getColorValue('disabledContent')};
    background-color: ${getColorValue('disabledBackground')};
  }

  &:disabled:hover {
    box-shadow: inset 0 0 0 var(--lns-formFieldBorderWidth)
      var(--lns-color-formFieldBorder);
  }

  &::placeholder {
    color: ${getColorValue('bodyDimmed')};
  }
`;

const IconSection = styled.div`
  position: absolute;
  pointer-events: none;
  width: ${u(6)};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
`;

const AddOnSection = styled.div`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 0;
  width: ${u(6)};
  top: 50%;
  transform: translateY(-50%);
`;

const TextOverlay = styled.div<{ hasIcon?: boolean }>`
  position: absolute;
  top: 0;
  left: ${props =>
    props.hasIcon ? u(5.5) : 'var(--lns-formFieldHorizontalPadding)'};
  right: ${u(5.5)};
  bottom: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
  color: inherit;
`;

// Scale down proportionally (preserving aspect ratio) and crop overflow
const TypeaheadImage = styled.img<{ isDisabled?: boolean }>`
  height: 100%;
  width: auto;
  min-width: 100%;
  min-height: 100%;
  object-fit: cover;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
`;

const TypeaheadHeader = ({
  selectedItem,
  selectedOptionValue,
  placeholder,
  isDisabled,
  options,
  getInputProps,
  ariaMenuName,
  isOpen,
  onInputFocus,
  hasError,
  hasLoader,
  inputValue,
  handleInputValueChange,
  inputRef,
  id,
  onKeyDown,
}: TypeaheadHeaderProps) => {
  const icon = getIcon({ options, selectedItem, selectedOptionValue });
  const hasIcon = Boolean(icon);
  const color = isDisabled ? 'disabledContent' : undefined;

  const handleClickAndFocus = () => {
    if (!isDisabled) {
      onInputFocus();
    }
  };

  const baseInputProps = getInputProps({
    id,
    'aria-labelledby': undefined,
    disabled: isDisabled,
    onFocus: handleClickAndFocus,
    onClick: handleClickAndFocus,
    value: inputValue,
    onBlur: () => {
      handleInputValueChange('');
    },
    onChange: e => handleInputValueChange(e.target.value),
  });

  const inputProps = {
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-haspopup': 'listbox',
    'aria-expanded': isOpen,
    ...getHeaderAccessibilityProps({
      selectedItem,
      getInputProps,
      ariaMenuName,
    }),
    ...baseInputProps,
    onKeyDown: (e: React.KeyboardEvent) => {
      onKeyDown?.(e);
      baseInputProps.onKeyDown?.(e);
    },
  };

  const showPlaceholder = !inputValue && !selectedOptionValue;

  return (
    <TypeaheadHeaderWrapper onClick={handleClickAndFocus} disabled={isDisabled}>
      {hasIcon && (
        <IconSection>
          {typeof icon === 'string' ? (
            <Container radius="50" width={3} height={3} overflow="hidden">
              <Align alignment="center">
                <TypeaheadImage src={icon} alt="" isDisabled={isDisabled} />
              </Align>
            </Container>
          ) : (
            <Icon icon={icon} color={color} />
          )}
        </IconSection>
      )}

      <TypeaheadInputField
        ref={inputRef}
        {...inputProps}
        hasIcon={hasIcon}
        hasAddOn={true}
        hasError={hasError}
        isDisabled={isDisabled}
      />

      {!inputValue && (
        <TextOverlay hasIcon={hasIcon}>
          <Text hasEllipsis color={showPlaceholder ? 'bodyDimmed' : 'inherit'}>
            {getTitle({
              options,
              selectedItem,
              selectedOptionValue,
              placeholder,
            })}
          </Text>
        </TextOverlay>
      )}

      <AddOnSection>
        {hasLoader ? (
          <Loader size="small" />
        ) : (
          <Icon icon={<SvgChevronDown />} color={color} />
        )}
      </AddOnSection>
    </TypeaheadHeaderWrapper>
  );
};

const getSelectedOption = (
  value: string | null,
  options: OptionsObject[] | GroupedOptionsObject[]
): OptionsObject | null => {
  if (isGrouped(options)) {
    for (const option of options) {
      const foundItem = option.items.find(item => item.value === value);
      if (foundItem) {
        return foundItem;
      }
    }
  } else {
    return options.find(item => item.value === value) ?? null;
  }

  return null;
};

const didSelectedOptionValueChange = (
  selectedOptionValue: string | null,
  prevSelectedItem: OptionsObject | null
) => {
  const didChange = (selectedOptionValue || null) != prevSelectedItem?.value;

  return didChange;
};

const renderOption = (
  item,
  index,
  selectedItem,
  selectedOptionValue,
  highlightedIndex,
  getItemProps,
  keyboardMove,
  setKeyboardMove
) => {
  const isSelected =
    (!selectedItem && item.value === selectedOptionValue) ||
    (selectedItem && selectedItem.value === item.value);
  return (
    <MenuItem
      key={index}
      getItemProps={getItemProps}
      icon={item.icon}
      hidden={item.hidden}
      {...getItemProps({
        key: `${item.value}-${index}`,
        index,
        item,
        disabled: item.isDisabled,
        'aria-selected': isSelected,
        onMouseMove: () => {
          if (keyboardMove) {
            setKeyboardMove(false);
          }
        },
      })}
      isDisabled={item.isDisabled}
      hasDivider={item.hasDivider}
      isHighlighted={highlightedIndex === index}
      keyboardMove={keyboardMove && highlightedIndex === index}
      isSelected={isSelected}
    >
      {item.title}
    </MenuItem>
  );
};

const TypeaheadMenu = ({
  options,
  selectedItem,
  selectedOptionValue,
  highlightedIndex,
  getItemProps,
  keyboardMove,
  setKeyboardMove,
  isLoading,
  loadingMessage,
  emptyResultsMessage,
  hasAvailableOptions,
  ...menuProps
}) => {
  const defaultLoadingMessage = loadingMessage || <Loader />;
  const defaultEmptyResultsMessage = emptyResultsMessage || 'No results';

  if (isLoading) {
    return (
      <Menu as="div" {...menuProps}>
        <Container padding="large">
          <Align alignment="center">{defaultLoadingMessage}</Align>
        </Container>
      </Menu>
    );
  }

  if (!hasAvailableOptions) {
    return (
      <Menu as="div" {...menuProps}>
        <Container padding="large">
          <Align alignment="center">{defaultEmptyResultsMessage}</Align>
        </Container>
      </Menu>
    );
  }

  if (!isGrouped(options)) {
    return (
      <Menu {...menuProps}>
        {options.map((item, index) =>
          renderOption(
            item,
            index,
            selectedItem,
            selectedOptionValue,
            highlightedIndex,
            getItemProps,
            keyboardMove,
            setKeyboardMove
          )
        )}
      </Menu>
    );
  }

  let downshiftIndex = 0;
  return (
    <Menu {...menuProps}>
      {options.map(group => {
        const groupId = `group-${group.group.replace(/\s+/g, '-')}`;
        return (
          <li key={groupId}>
            <Spacer left="medium" top="small" bottom="xsmall">
              <Text
                id={groupId}
                size="body-sm"
                fontWeight="bold"
                htmlTag={group.headingTag || 'h2'}
              >
                {group.group}
              </Text>
            </Spacer>
            <Group role="group" aria-labelledby={groupId}>
              {group.items.map(item => {
                return renderOption(
                  item,
                  downshiftIndex++,
                  selectedItem,
                  selectedOptionValue,
                  highlightedIndex,
                  getItemProps,
                  keyboardMove,
                  setKeyboardMove
                );
              })}
            </Group>
          </li>
        );
      })}
    </Menu>
  );
};

const Typeahead = forwardRef(
  (
    {
      container,
      onOptionChange,
      onInputValueChange,
      menuZIndex = 1100,
      menuMaxWidth,
      menuMaxHeight = 34,
      menuMinWidth,
      ariaMenuName,
      selectedOptionValue,
      onOuterClick,
      options,
      placeholder,
      menuPosition = 'left',
      isDisabled,
      onOpenChange,
      isLoading,
      loadingMessage,
      emptyResultsMessage,
      errorMessage,
      id,
      ...props
    }: TypeaheadProps &
      Omit<
        React.ComponentProps<typeof TypeaheadWrapper>,
        keyof TypeaheadProps
      > &
      Omit<
        LensDivProps,
        keyof TypeaheadProps & React.ComponentProps<typeof TypeaheadWrapper>
      >,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const environment = getDownshiftEnvironment(container);
    const internalInputRef = useRef<HTMLInputElement>(null);

    const inputRef = useCallback(
      (node: HTMLInputElement) => {
        internalInputRef.current = node;
        if (ref) {
          typeof ref === 'function' ? ref(node) : (ref.current = node);
        }
      },
      [ref]
    );

    const [inputValue, setInputValue] = useState<string>('');
    const [downshiftIsOpen, setDownshiftIsOpen] = useState<boolean>(false);
    const [keyboardMove, setKeyboardMove] = useState(false);
    const [prevSelectedItem, setPrevSelectedItem] =
      useState<OptionsObject | null>(
        getSelectedOption(selectedOptionValue, options)
      );
    const [selectedItem, setSelectedItem] = useState<OptionsObject | null>(
      prevSelectedItem
    );

    const handleInputValueChange = (value: string) => {
      setInputValue(value);
      onInputValueChange && onInputValueChange(value);
    };

    const onDownshiftChange = item => {
      const value = item || null;

      setSelectedItem(value);
      onOptionChange && onOptionChange(value);
    };

    const downshiftProps = {
      itemToString: item => (item ? item.value : ''),
      onChange: onDownshiftChange,
      onOuterClick,
      environment,
      selectedItem,
      isOpen: downshiftIsOpen,
    };

    if (environment) {
      downshiftProps.environment = environment;
    }

    const { layerProps, triggerProps, renderLayer, triggerBounds } = useLayer({
      isOpen: downshiftIsOpen,
      container,
      ResizeObserver,
      placement: menuPositions[menuPosition],
      auto: true,
      snap: true,
    });

    useEffect(() => {
      if (didSelectedOptionValueChange(selectedOptionValue, prevSelectedItem)) {
        const item = getSelectedOption(selectedOptionValue, options);

        setPrevSelectedItem(item);
        setSelectedItem(item);
        // Reset the input value when the selected option value changes
        setInputValue('');
      }
    }, [selectedOptionValue, options, prevSelectedItem]);

    useEffect(() => {
      onOpenChange && onOpenChange(downshiftIsOpen);
      if (!downshiftIsOpen) {
        internalInputRef.current?.blur();
      }
    }, [downshiftIsOpen, onOpenChange]);

    const hasAvailableOptions = useMemo(() => {
      if (!Array.isArray(options)) {
        return false;
      }

      return isGrouped(options)
        ? options.some(group => group.items.length > 0)
        : options.length > 0;
    }, [options]);

    const stateReducer = (state, changes) => {
      if (changes.isOpen !== undefined) {
        if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
          setDownshiftIsOpen(false);

          return { isOpen: false };
        }

        setDownshiftIsOpen(changes.isOpen);
      }

      return changes;
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'Enter':
        case ' ':
        case 'Tab':
        case 'Escape':
          setKeyboardMove(true);
          break;
        default:
          break;
      }
    };

    return (
      <TypeaheadWrapper {...props}>
        <Downshift {...downshiftProps} stateReducer={stateReducer}>
          {({
            getItemProps,
            getInputProps,
            getMenuProps,
            isOpen,
            highlightedIndex,
            selectedItem,
          }) => {
            return (
              // Don't set the role to combobox to ensure a consistent experience for screen readers
              <div role="presentation">
                <div {...triggerProps}>
                  <TypeaheadHeader
                    inputRef={inputRef}
                    selectedItem={selectedItem}
                    selectedOptionValue={selectedOptionValue}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    options={options}
                    getInputProps={getInputProps}
                    ariaMenuName={ariaMenuName}
                    inputValue={inputValue}
                    handleInputValueChange={handleInputValueChange}
                    isOpen={downshiftIsOpen && isOpen}
                    onInputFocus={() => {
                      setDownshiftIsOpen(true);
                    }}
                    hasLoader={isLoading}
                    hasError={Boolean(errorMessage)}
                    id={id}
                    onKeyDown={onKeyDown}
                  />
                </div>
                {downshiftIsOpen &&
                  isOpen &&
                  renderLayer(
                    <div
                      {...layerProps}
                      style={{
                        ...layerProps.style,
                        zIndex: menuZIndex,
                        width: menuMinWidth ? 'auto' : triggerBounds?.width,
                      }}
                    >
                      <TypeaheadMenu
                        options={options}
                        selectedItem={selectedItem}
                        selectedOptionValue={selectedOptionValue}
                        hasAvailableOptions={hasAvailableOptions}
                        highlightedIndex={highlightedIndex}
                        getItemProps={getItemProps}
                        keyboardMove={keyboardMove}
                        setKeyboardMove={setKeyboardMove}
                        isLoading={isLoading}
                        loadingMessage={loadingMessage}
                        emptyResultsMessage={emptyResultsMessage}
                        position={menuPosition}
                        downshiftMenuProps={() => getMenuProps({ onKeyDown })}
                        maxWidth={menuMaxWidth}
                        maxHeight={menuMaxHeight}
                        minWidth={menuMinWidth}
                      />
                    </div>
                  )}
                {Boolean(errorMessage) && !downshiftIsOpen ? (
                  <ErrorMessage>{errorMessage}</ErrorMessage>
                ) : null}
              </div>
            );
          }}
        </Downshift>
      </TypeaheadWrapper>
    );
  }
);

Typeahead.displayName = 'Typeahead';

export type OptionsObject = {
  value: string;
  title: React.ReactNode;
  icon?: React.ReactNode | string;
  isDisabled?: boolean;
  hasDivider?: boolean;
  hidden?: HTMLAttributes<HTMLOptionElement>['hidden'];
};

export type GroupedOptionsObject = {
  group: string;
  items: OptionsObject[];
  headingTag?: 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span';
};

type TypeaheadProps = {
  menuZIndex?: number;
  menuMaxHeight?: string | number;
  menuPosition?: 'left' | 'right' | 'topRight' | 'topLeft';

  options: OptionsObject[] | GroupedOptionsObject[];
  selectedOptionValue: string | null;
  placeholder?: React.ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  loadingMessage?: React.ReactNode;
  emptyResultsMessage?: React.ReactNode;
  errorMessage?: string;

  container?: HTMLElement;
  ariaMenuName?: string;
  menuMaxWidth?: string | number;
  menuMinWidth?: string | number;

  onInputValueChange?: (inputValue: string) => void;
  onOptionChange?: (selectedOption: OptionsObject | null) => void;
  onOpenChange?: (isOpen: boolean) => void;
  onOuterClick?: () => void;
};

type TypeaheadHeaderProps = {
  selectedItem?: { icon: React.ReactNode; title: string };
  selectedOptionValue?: string;
  placeholder?: React.ReactNode;
  isDisabled?: boolean;
  options?: OptionsObject[] | GroupedOptionsObject[];
  getInputProps?: any;
  ariaMenuName?: string;
  isOpen: boolean;
  onInputFocus: () => void;
  hasError?: boolean;
  hasLoader?: boolean;
  inputValue?: string;
  handleInputValueChange?: (inputValue: string) => void;
  inputRef?: React.RefCallback<HTMLInputElement>;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
};

export default Typeahead;
